import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Main from "./components/Main";
import AdminPanel from "./components/AdminPanel";
import UserProfile from "./components/UserProfile";
import Chatbot from "./components/Chatbot";
import ChatbotPage from "./components/ChatbotPage";
import Loader from "./components/Loader";
import API_URL from "./components/config";

// Создаем контекст авторизации
export const AuthContext = createContext(null);

// Провайдер контекста авторизации
export const AuthProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Добавляем состояние для чат-бота
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isChatbotFullscreen, setIsChatbotFullscreen] = useState(false);
  
  // Добавляем механизм предотвращения частых проверок
  const lastAuthCheck = useRef(0);
  const isCheckingAuth = useRef(false);
  const authTimeout = useRef(null);

  // Функция проверки авторизации с защитой от частых повторных вызовов
  const checkAuth = async (force = false) => {
    // Пропускаем проверку, если она уже выполняется
    if (isCheckingAuth.current) {
      console.log("Проверка авторизации уже выполняется, пропускаем...");
      return;
    }
    
    // Проверяем, прошло ли достаточно времени с последней проверки (минимум 5 секунд)
    const now = Date.now();
    if (!force && lastAuthCheck.current > 0 && now - lastAuthCheck.current < 5000) {
      console.log("Последняя проверка авторизации была менее 5 секунд назад, пропускаем...");
      return;
    }
    
    // Устанавливаем флаг, что проверка выполняется
    isCheckingAuth.current = true;
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("access_token");
      
      if (!token) {
        setIsAuthenticated(false);
        setUserRole(0);
        setIsLoading(false);
        setAuthChecked(true);
        lastAuthCheck.current = now;
        isCheckingAuth.current = false;
        return;
      }

      // Используем эндпоинт для проверки авторизации
      const res = await fetch(`${API_URL}/current`, {
        method: "GET",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json" 
        },
        credentials: "include",
      });

      if (res.ok) {
        const userData = await res.json();
        console.log("Авторизация успешна, данные пользователя:", userData);
        setIsAuthenticated(true);
        setUserRole(userData.role_id || 1);
        
        // Кэшируем данные пользователя в sessionStorage
        sessionStorage.setItem('cachedUserData', JSON.stringify({
          isAuthenticated: true,
          userRole: userData.role_id || 1,
          timestamp: now
        }));
      } else {
        // Если ответ не OK, проверяем статус
        console.error("Ошибка проверки авторизации:", res.status);
        
        // Если токен просрочен или недействителен, пробуем обновить его
        if (res.status === 401) {
          // Здесь можно добавить обработку refresh token, если есть
          console.log("Токен недействителен, выход из системы");
          localStorage.removeItem("access_token");
          sessionStorage.removeItem('cachedUserData');
        }
        
        setIsAuthenticated(false);
        setUserRole(0);
      }
    } catch (error) {
      console.error("Ошибка при проверке авторизации:", error);
      setIsAuthenticated(false);
      setUserRole(0);
    } finally {
      setIsLoading(false);
      setAuthChecked(true);
      lastAuthCheck.current = now;
      isCheckingAuth.current = false;
    }
  };

  // Функция для выхода из системы
  const logout = async () => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const response = await fetch(`${API_URL}/auth/jwt/logout`, {
          method: "POST",
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json" 
          },
          credentials: "include",
        });
        
        if (response.ok) {
          console.log("Успешный выход из системы");
        } else {
          console.error("Ошибка при выходе из системы:", response.status);
        }
      } catch (error) {
        console.error("Ошибка при выходе из системы:", error);
      }
    }
    
    localStorage.removeItem("access_token");
    sessionStorage.removeItem('cachedUserData');
    setIsAuthenticated(false);
    setUserRole(0);
  };

  // Функция для обновления состояния после входа
  const login = async (token) => {
    if (token) {
      localStorage.setItem("access_token", token);
      // Очищаем кэш при входе, чтобы получить свежие данные
      sessionStorage.removeItem('cachedUserData');
    }
    await checkAuth(true);
  };

  // Проверяем авторизацию при загрузке
  useEffect(() => {
    // Сначала пробуем восстановить данные из кэша
    const cachedData = sessionStorage.getItem('cachedUserData');
    
    if (cachedData) {
      try {
        const { isAuthenticated: cachedAuth, userRole: cachedRole, timestamp } = JSON.parse(cachedData);
        
        // Проверяем, не устарел ли кэш (максимум 15 минут)
        const now = Date.now();
        const cacheMaxAge = 15 * 60 * 1000; // 15 минут
        
        if (now - timestamp < cacheMaxAge) {
          console.log("Используем кэшированные данные авторизации");
          setIsAuthenticated(cachedAuth);
          setUserRole(cachedRole);
          setIsLoading(false);
          setAuthChecked(true);
          
          // Планируем фоновую проверку через некоторое время
          authTimeout.current = setTimeout(() => {
            checkAuth(true);
          }, 1000); // Проверяем через 1 секунду после загрузки
          
          return;
        } else {
          console.log("Кэш устарел, выполняем новую проверку авторизации");
        }
      } catch (error) {
        console.error("Ошибка при чтении кэша авторизации:", error);
      }
    }
    
    // Если кэш отсутствует или устарел, выполняем проверку
    checkAuth(true);
    
    // Отслеживаем изменения в localStorage
    const handleStorageChange = (e) => {
      if (e.key === "access_token") {
        checkAuth(true);
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    // Периодически проверяем статус авторизации (каждые 15 минут)
    const authCheckInterval = setInterval(() => {
      checkAuth(true);
    }, 15 * 60 * 1000);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(authCheckInterval);
      if (authTimeout.current) {
        clearTimeout(authTimeout.current);
      }
    };
  }, []);

  // Функция проверки доступа к чат-боту (доступен пользователям с ролью > 1)
  const canAccessChatbot = () => {
    return isAuthenticated && userRole > 1;
  };

  // Функции для управления чат-ботом
  const openChatbot = (fullscreen = false) => {
    setIsChatbotOpen(true);
    setIsChatbotFullscreen(fullscreen);
  };

  // Функция для закрытия чат-бота с очисткой всех блокировок прокрутки
  const closeChatbot = () => {
    console.log("closeChatbot вызван");
    setIsChatbotOpen(false);
    
    // Восстанавливаем прокрутку только если она была заблокирована
    if (document.body.dataset.scrollBlocked === 'true') {
      const scrollY = parseInt(document.body.dataset.scrollY || '0');
      
      // Очищаем все стили, блокирующие прокрутку
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
      
      // Восстанавливаем позицию прокрутки
      setTimeout(() => {
        window.scrollTo({
          top: scrollY,
          behavior: 'instant'
        });
        // Дополнительная проверка и восстановление прокрутки
        if (window.scrollY !== scrollY) {
          window.scrollTo(0, scrollY);
        }
        // Очищаем сохраненные данные
        delete document.body.dataset.scrollY;
        delete document.body.dataset.scrollBlocked;
      }, 50);
    } else {
      // Просто убираем возможные стили без изменения позиции прокрутки
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
    }
  };

  const toggleChatbotFullscreen = () => {
    setIsChatbotFullscreen(!isChatbotFullscreen);
  };

  // НОВАЯ функция для открытия/закрытия чат-бота с учетом типа устройства
  const toggleChat = () => {
    console.log("toggleChat вызван");
    
    // Определяем тип устройства
    const isMobile = window.innerWidth <= 768;
    console.log("isMobile:", isMobile);
    
    if (isMobile) {
      // На мобильных устройствах всегда открываем в полноэкранном режиме
      console.log("Мобильное устройство: открываем в полноэкранном режиме");
      setIsChatbotOpen(true);
      setIsChatbotFullscreen(true);
      
      // Сохраняем текущую позицию прокрутки перед блокировкой
      const scrollY = window.scrollY;
      document.body.dataset.scrollY = scrollY.toString();
      document.body.dataset.scrollBlocked = 'true';
      
      // Дополнительно блокируем прокрутку
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
    } else {
      // На десктопе просто переключаем видимость чат-бота
      console.log("Десктоп: переключаем состояние чат-бота:", !isChatbotOpen);
      setIsChatbotOpen(!isChatbotOpen);
      setIsChatbotFullscreen(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        isLoading,
        authChecked,
        checkAuth,
        login,
        logout,
        canAccessChatbot,
        // Добавляем новые поля и функции
        isChatbotOpen,
        isChatbotFullscreen,
        openChatbot,
        closeChatbot,
        toggleChatbotFullscreen,
        toggleChat
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Хук для использования контекста авторизации
export const useAuth = () => useContext(AuthContext);

// Компонент-обертка для перехвата маршрутов
const AppRoutes = () => {
  const navigate = useNavigate();
  const { toggleChat } = useAuth();
  const location = useLocation();

  // Эффект для проверки маршрута при каждом изменении URL
  useEffect(() => {
    // Проверяем, является ли устройство мобильным
    const isMobile = window.innerWidth <= 768;
    
    // Если мы на странице /chatbot и это мобильное устройство
    if (location.pathname === '/chatbot' && isMobile) {
      console.log('Перехватываем переход на /chatbot на мобильном устройстве');
      
      // Сохраняем текущую позицию прокрутки
      const scrollY = window.scrollY;
      sessionStorage.setItem('chatbot_return_position', scrollY.toString());
      
      // Переходим на главную
      navigate('/');
      
      // И открываем чат-бот через toggleChat
      setTimeout(() => {
        toggleChat();
      }, 100);
    }
    
    // Если пользователь переходит с полноэкранного чата на главную напрямую
    if (location.pathname === '/' && sessionStorage.getItem('chatbot_scroll_position')) {
      const savedPosition = sessionStorage.getItem('chatbot_scroll_position');
      if (savedPosition) {
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedPosition),
            behavior: 'instant'
          });
          sessionStorage.removeItem('chatbot_scroll_position');
        }, 50);
      }
    }
  }, [location, navigate, toggleChat]);

  return (
    <Routes>
      <Route path="/" element={<Main />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/profile" element={<UserProfile />} />
      <Route path="/chatbot" element={<ChatbotPage />} />
    </Routes>
  );
};

function App() {
  // Создаем состояние для контроля отображения глобального лоадера
  const [appLoading, setAppLoading] = useState(true);

  useEffect(() => {
    // Функция для проверки загрузки страницы
    const checkResourcesLoaded = () => {
      // Ждем загрузку всех важных ресурсов страницы
      if (document.readyState === 'complete') {
        // Оставляем лоадер на некоторое время для лучшего UX
        setTimeout(() => {
          setAppLoading(false);
        }, 1000); // 1 секунда после полной загрузки
      } else {
        window.addEventListener('load', () => {
          setTimeout(() => {
            setAppLoading(false);
          }, 1000);
        });
      }
    };

    // Начинаем проверку загрузки
    checkResourcesLoaded();

    // Таймаут для максимального времени отображения лоадера (5 секунд)
    const maxLoadingTime = setTimeout(() => {
      setAppLoading(false);
    }, 5000);

    return () => {
      clearTimeout(maxLoadingTime);
      window.removeEventListener('load', checkResourcesLoaded);
    };
  }, []);

  return (
    <Router>
      {/* Лоадер отображается только пока идет загрузка */}
      {appLoading && <Loader />}
      
      <AuthProvider>
        <AppRoutes />
        <Chatbot />
      </AuthProvider>
    </Router>
  );
}

export default App;
